import { Box } from "@chakra-ui/react";
import { ReactNode, useCallback } from "react";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { BannerSelection } from "src/components/Table/BannerSelection";
import { Selection } from "src/hooks/useMultiselectState";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import { Permission } from "src/types/permissions";
import { BulkTagsPopover } from "../../tags/BulkTagsPopover";
import { BulkTagsPopoverAsync } from "../../tags/BulkTagsPopoverAsync";
import {
  FormIdListResult,
  FormListRow,
  SchoolFormId,
  SchoolFormKeyRecord,
  SchoolFormKeyRecordFactory,
} from "../types";
import { BulkActionTargetsProvider } from "./BulkActionTargetsProvider";
import { BulkExportButton } from "./BulkExportButton";
import { MoreMenu } from "./BulkMoreMenu";
import { BulkVerifyButton } from "./BulkVerifyButton";
import { BulkVerifyButtonAsync } from "./BulkVerifyButtonAsync";
import { FormStatusMenuBulk } from "./FormStatusMenuBulk";
import { FormStatusMenuBulkAsync } from "./FormStatusMenuBulkAsync";
import { NextStepMenuBulk } from "./NextStepMenuBulk";
import { SendMessageButton } from "./SendMessageButton";
import { SetVisibilityMenuBulk } from "./SetVisibilityMenuBulk";
import { SetVisibilityMenuBulkAsync } from "./SetVisibilityMenuBulkAsync";
import { MenuItem } from "./menuItem";

interface TableBannerProps {
  dropoffFormTemplateId?: uuid;
  formTemplate: GQL.FormTemplateFragment;
  enrollmentPeriodId: uuid;
  includeUpdateStatus: boolean;
  onFetchAllIds: () => Promise<FormIdListResult>;
  onRefetch: () => Promise<unknown>;
  selection: Selection<SchoolFormKeyRecord, FormListRow>;
  onClearSelection: () => void;
  hiddenMenuItems: MenuItem[];
}

export const TableBanner: React.FC<TableBannerProps> = ({
  dropoffFormTemplateId,
  formTemplate,
  enrollmentPeriodId,
  includeUpdateStatus,
  onFetchAllIds,
  onRefetch,
  selection,
  onClearSelection,
  hiddenMenuItems,
}) => {
  const flags = useFlags([
    "tagging-abac",
    "school-admin-status-edit",
    "async-bulk-actions",
  ]);
  const asyncBulkActions = flags["async-bulk-actions"].enabled;

  const formSchoolTagPermissions: Permission[] = flags["tagging-abac"].enabled
    ? ["form.school.tag:create", "form.school.tag:update"]
    : ["form:update"];

  /** Materialize selected keys for sync bulk actions (matches main). */
  const fetchSelectedFormKeys = useCallback(async (): Promise<
    SchoolFormId[]
  > => {
    return selection.materializeKeys(async () =>
      (await onFetchAllIds()).items.map(SchoolFormKeyRecordFactory)
    );
  }, [onFetchAllIds, selection]);

  const hasEditStatusPermissionAdmins = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.ORG_ADMIN,
    HasuraRole.DISTRICT_ADMIN,
  ]);

  // hard-coding to allow full school admin edit permissions based on feature flag
  // TODO: remove when custom user roles allow for school admin to configure more granular form edit permissions
  const hasEditStatusPermissionSchoolAdmin =
    useRequiredHasuraRoles([HasuraRole.SCHOOL_ADMIN]) &&
    flags["school-admin-status-edit"].enabled;

  const hasEditStatusPermission =
    hasEditStatusPermissionAdmins || hasEditStatusPermissionSchoolAdmin;

  return (
    <BulkActionTargetsProvider
      selection={selection}
      formTemplateId={formTemplate.id}
      fetchAllIds={onFetchAllIds}
      onClearSelection={onClearSelection}
    >
      <BannerSelection
        selection={selection}
        overflowMenu={(children: ReactNode) => {
          return (
            <MoreMenu
              formTemplateId={formTemplate.id}
              hiddenMenuItems={hiddenMenuItems}
              fetchSelectedFormKeys={fetchSelectedFormKeys}
              onRefetch={onRefetch}
            >
              {children}
            </MoreMenu>
          );
        }}
      >
        <WithUserPermissions permissions={["message:create"]}>
          <SendMessageButton fetchSelectedFormKeys={fetchSelectedFormKeys} />
        </WithUserPermissions>

        {!hiddenMenuItems.includes(MenuItem.Export) && (
          <WithUserPermissions permissions={["form:read"]}>
            <BulkExportButton
              formTemplate={formTemplate}
              onFetchAllIds={onFetchAllIds}
              selection={selection}
              dropoffFormTemplateId={dropoffFormTemplateId}
            />
          </WithUserPermissions>
        )}

        {!hiddenMenuItems.includes(MenuItem.BulkVerify) && (
          <WithUserPermissions permissions={["form:update"]}>
            {asyncBulkActions ? (
              <BulkVerifyButtonAsync
                formTemplate={formTemplate}
                onFetchAllIds={onFetchAllIds}
                selection={selection}
              />
            ) : (
              <BulkVerifyButton
                formTemplate={formTemplate}
                onFetchAllIds={onFetchAllIds}
                selection={selection}
              />
            )}
          </WithUserPermissions>
        )}

        {!hiddenMenuItems.includes(MenuItem.BulkStatusUpdate) && (
          <WithUserPermissions permissions={["form:update"]}>
            {includeUpdateStatus && hasEditStatusPermission && (
              <Box>
                {asyncBulkActions ? (
                  <FormStatusMenuBulkAsync />
                ) : (
                  <FormStatusMenuBulk
                    fetchSelectedFormKeys={fetchSelectedFormKeys}
                    refetchForms={onRefetch}
                  />
                )}
              </Box>
            )}
          </WithUserPermissions>
        )}

        {!hiddenMenuItems.includes(MenuItem.BulkStatusDescriptionUpdate) && (
          <WithUserPermissions permissions={["form:update"]}>
            {hasEditStatusPermission && (
              <Box>
                <NextStepMenuBulk
                  formTemplateId={formTemplate.id}
                  fetchSelectedFormKeys={fetchSelectedFormKeys}
                />
              </Box>
            )}
          </WithUserPermissions>
        )}

        {!hiddenMenuItems.includes(MenuItem.Tag) && (
          <WithUserPermissions permissions={formSchoolTagPermissions}>
            {asyncBulkActions ? (
              <BulkTagsPopoverAsync enrollmentPeriodId={enrollmentPeriodId} />
            ) : (
              <BulkTagsPopover
                enrollmentPeriodId={enrollmentPeriodId}
                fetchSelectedFormKeys={fetchSelectedFormKeys}
                refetchForms={onRefetch}
              />
            )}
          </WithUserPermissions>
        )}

        {!hiddenMenuItems.includes(MenuItem.BulkSetVisibility) && (
          <WithUserPermissions permissions={["form:update"]}>
            {hasEditStatusPermission && (
              <Box>
                {asyncBulkActions ? (
                  <SetVisibilityMenuBulkAsync />
                ) : (
                  <SetVisibilityMenuBulk
                    fetchSelectedFormKeys={fetchSelectedFormKeys}
                    refetchForms={onRefetch}
                  />
                )}
              </Box>
            )}
          </WithUserPermissions>
        )}
      </BannerSelection>
    </BulkActionTargetsProvider>
  );
};
